/** Login-Ansicht: Command-Net Secure Terminal. */

import { useSignal } from "@preact/signals";
import { useEffect, useRef } from "preact/hooks";
import { login } from "../store";
import { t } from "../i18n";
import { buildInfo, formatBuildInfo } from "../build-info";

/** Event-Name für den Warp-Push (Username → Passwort); Canvas hört darauf. */
const WARP_EVENT = "command-net:login-warp";

/** Ein Stern im 3D-Raum: Welt-x/y, Tiefe z (1 = fern, →0 = an der Kamera). */
type Star = {
  x: number;
  y: number;
  z: number;
  r: number;        // Basisradius in px (nah)
  c: string;        // "r,g,b"
  twA: number;      // Twinkle-Amplitude (0 = ruhig)
  twP: number;      // Twinkle-Phase
  px: number | null; // letzte Screen-Position (für Warp-Streaks)
  py: number | null;
};

const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);

/** Sternfarbe: überwiegend blau-weiß, wenige Cyan-, sehr wenige warme Akzente. */
function starColor(): string {
  const p = Math.random();
  if (p < 0.05) return "255,196,150";                       // warm, selten
  if (p < 0.16) return "72,230,255";                        // cyan-Akzent
  const w = 200 + Math.floor(Math.random() * 45);           // blau-weiß
  return `${w},${Math.min(w + 14, 250)},255`;
}

function spawnStar(initial: boolean): Star {
  const large = Math.random() < 0.06;
  const medium = !large && Math.random() < 0.3;
  return {
    // Leicht überstreut (×1.15), damit auch die Ränder besetzt sind.
    x: (Math.random() * 2 - 1) * 1.15,
    y: (Math.random() * 2 - 1) * 1.15,
    // Initial über die volle Tiefe verteilt, danach immer fern respawnen.
    z: initial ? 0.06 + Math.random() * 0.94 : 0.92 + Math.random() * 0.08,
    r: large ? 1.5 + Math.random() * 0.8 : medium ? 0.85 + Math.random() * 0.55 : 0.4 + Math.random() * 0.4,
    c: starColor(),
    twA: large || medium ? 0 : Math.random() * 0.22,
    twP: Math.random() * Math.PI * 2,
    px: null,
    py: null,
  };
}

/**
 * Deep-Space-Flug als 2D-Canvas (rAF): echte Z-Projektion → Parallax/Tiefe
 * entsteht aus der Geometrie, nicht aus gekachelten Layern (keine Muster,
 * keine Partikel-Pärchen). Pausiert bei verstecktem Tab; `html.motion-reduce`
 * rendert ein statisches Sternfeld ohne Loop.
 */
function LoginGalaxy() {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = 0, h = 0, cx = 0, cy = 0;
    let stars: Star[] = [];

    function resize() {
      if (!canvas || !ctx) return;
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cx = w / 2;
      cy = h * 0.46; // Fluchtpunkt knapp über der Mitte — hinter der Card
      const count = clamp(Math.round((w * h) / 4200), 220, 520);
      while (stars.length < count) stars.push(spawnStar(true));
      if (stars.length > count) stars.length = count;
      for (const s of stars) { s.px = null; s.py = null; }
    }

    // Grundtempo ruhig; der Warp legt eine kurze Boost-Hüllkurve darüber
    // (schneller Attack, weicher Decay) — kein harter Cut.
    const BASE_SPEED = 0.055;
    const WARP_PEAK = 0.6;
    let warpT = Infinity;

    function warpBoost(): number {
      if (!isFinite(warpT)) return 0;
      if (warpT < 0.16) { const a = warpT / 0.16; return WARP_PEAK * a * a * (3 - 2 * a); }
      return WARP_PEAK * Math.exp(-(warpT - 0.16) / 0.5);
    }

    function step(dt: number, now: number) {
      if (!ctx) return;
      if (isFinite(warpT)) warpT += dt;
      const speed = BASE_SPEED + warpBoost();
      const streaking = speed > 0.14;
      ctx.clearRect(0, 0, w, h);

      for (const s of stars) {
        s.z -= speed * dt;
        if (s.z <= 0.035) { Object.assign(s, spawnStar(false)); continue; }
        const k = 1 / s.z;
        const sx = cx + s.x * cx * k;
        const sy = cy + s.y * cy * k;
        if (sx < -60 || sx > w + 60 || sy < -60 || sy > h + 60) { Object.assign(s, spawnStar(false)); continue; }

        const depth = 1 - s.z; // 0 fern → 1 nah
        const rad = s.r * (0.3 + 0.95 * depth);
        let a = clamp(depth * 1.3, 0.04, 1) * (0.55 + 0.45 * Math.min(rad, 1));
        if (s.twA > 0) a *= 1 - s.twA * (0.5 + 0.5 * Math.sin(now * 0.0016 + s.twP));

        if (streaking && s.px !== null && s.py !== null) {
          ctx.strokeStyle = `rgba(${s.c},${(a * 0.9).toFixed(3)})`;
          ctx.lineWidth = Math.max(rad * 1.4, 0.5);
          ctx.lineCap = "round";
          ctx.beginPath();
          ctx.moveTo(s.px, s.py);
          ctx.lineTo(sx, sy);
          ctx.stroke();
        } else {
          ctx.fillStyle = `rgba(${s.c},${a.toFixed(3)})`;
          ctx.beginPath();
          ctx.arc(sx, sy, rad, 0, Math.PI * 2);
          ctx.fill();
          if (s.r > 1.4) { // Halo nur für die wenigen großen Sterne
            ctx.fillStyle = `rgba(${s.c},${(a * 0.1).toFixed(3)})`;
            ctx.beginPath();
            ctx.arc(sx, sy, rad * 3.2, 0, Math.PI * 2);
            ctx.fill();
          }
        }
        s.px = sx;
        s.py = sy;
      }
    }

    resize();

    // Manuelles Motion-Opt-out (Repo-Konvention): ein statisches Sternfeld, kein Loop.
    if (document.documentElement.classList.contains("motion-reduce")) {
      step(0, 0);
      window.addEventListener("resize", resize);
      return () => window.removeEventListener("resize", resize);
    }

    let raf = 0;
    let last = performance.now();
    function frame(now: number) {
      const dt = Math.min((now - last) / 1000, 0.05); // Tab-Rückkehr: keine Riesenschritte
      last = now;
      step(dt, now);
      raf = requestAnimationFrame(frame);
    }
    raf = requestAnimationFrame(frame);

    function onWarp() { warpT = 0; }
    function onVisibility() {
      cancelAnimationFrame(raf);
      if (!document.hidden) {
        last = performance.now();
        for (const s of stars) { s.px = null; s.py = null; }
        raf = requestAnimationFrame(frame);
      }
    }
    window.addEventListener("resize", resize);
    window.addEventListener(WARP_EVENT, onWarp);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener(WARP_EVENT, onWarp);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return <canvas ref={ref} class="login-space" aria-hidden="true" />;
}

export function Login() {
  const user = useSignal("");
  const pw = useSignal("");
  const err = useSignal("");
  const busy = useSignal(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const lastWarp = useRef(0);
  const warpTimer = useRef(0);

  /** Warp-Push beim Wechsel ins Passwortfeld: Canvas + Card reagieren koordiniert. */
  function pushWarp() {
    if (!user.value.trim()) return;
    const now = Date.now();
    if (now - lastWarp.current < 1200) return;
    lastWarp.current = now;
    window.dispatchEvent(new Event(WARP_EVENT));
    const el = wrapRef.current;
    if (el) {
      clearTimeout(warpTimer.current);
      el.classList.remove("login-warp");
      void el.offsetWidth; // Reflow → Animation ist retriggerbar
      el.classList.add("login-warp");
      warpTimer.current = window.setTimeout(() => el.classList.remove("login-warp"), 850);
    }
  }

  useEffect(() => () => clearTimeout(warpTimer.current), []);

  async function submit(e: Event) {
    e.preventDefault();
    if (busy.value) return;
    busy.value = true;
    err.value = "";
    try {
      const r = await login(user.value.trim(), pw.value);
      if (!r.ok) err.value = String(r.error ?? t("login.errorFailed"));
    } catch {
      err.value = t("login.errorUnreachable");
    } finally {
      busy.value = false;
    }
  }

  return (
    <div class="login-wrap" ref={wrapRef}>
      <LoginGalaxy />
      <form class="card card-pad login-card stack" onSubmit={submit}>
        <div class="login-head">
          <div class="terminal-kicker"><span class="live-dot open" />{t("login.secureTerminal")}</div>
          <div class="logo-lg">
            {/* Lokales Logo; fehlt es, Bild ausblenden statt Broken-Image (kein Crash). */}
            <img src="/logo.png" alt="BF2142" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
          </div>
          <h1>COMMAND-NET</h1>
          <p class="caption">{t("login.subtitle")}</p>
        </div>
        {err.value && <div class="form-err">{err.value}</div>}
        <div class="field">
          <label for="u">{t("login.user")}</label>
          <input
            id="u"
            class="input"
            autocomplete="username"
            value={user.value}
            onInput={(e) => (user.value = (e.target as HTMLInputElement).value)}
          />
        </div>
        <div class="field">
          <label for="p">{t("login.password")}</label>
          <input
            id="p"
            class="input"
            type="password"
            autocomplete="current-password"
            value={pw.value}
            onFocus={pushWarp}
            onInput={(e) => (pw.value = (e.target as HTMLInputElement).value)}
          />
        </div>
        <button class="btn primary" type="submit" disabled={busy.value}>
          {busy.value ? "…" : t("login.submit")}
        </button>
      </form>
      {/* Dezente Release-Version (fixed unten, stört das Card-Centering nicht). */}
      <div class="login-buildinfo" aria-hidden="true">{formatBuildInfo(buildInfo.value)}</div>
    </div>
  );
}
